/**
 * Sincroniza o schedule a cada 15 minutos (com jitter de ±90s).
 * Também responde a refetches forçados via registerRefetchCallback e faz um sync inicial no mount.
 */
import { useEffect, useRef } from 'react'
import { scheduleManager } from '@/engine/schedule-manager'
import { deviceService } from '@/services/device.service'
import { useAuthStore } from '@/store/auth.store'
import { usePlayerStore } from '@/store/player.store'
import { playerLogger } from '@/utils/player-logger'
import { registerRefetchCallback } from './use-heartbeat'

const SYNC_INTERVAL_MS = 15 * 60 * 1000 // 15 min
const JITTER_MS = 90 * 1000 // ±90s

type OnScheduleUpdated = (version: string) => void

function jitter(): number {
  return (Math.random() * 2 - 1) * JITTER_MS
}

export function useScheduleSync(onUpdated: OnScheduleUpdated): void {
  const onUpdatedRef = useRef(onUpdated)
  onUpdatedRef.current = onUpdated

  useEffect(() => {
    const creds = useAuthStore.getState().credentials
    if (!creds) return

    let stopped = false
    let syncTimer: ReturnType<typeof setTimeout> | null = null
    // L-14 (revisão 2026-10-26): um refetch forçado durante um sync em andamento não dispara um
    // segundo sync em paralelo (que, no finally, criaria uma segunda cadeia de timers). Ele é
    // registrado aqui e executado logo que o sync atual termina — com o schedule mais recente.
    let inFlight = false
    let rerunRequested = false

    async function sync(): Promise<void> {
      if (stopped || !creds?.deviceId) return
      if (inFlight) {
        rerunRequested = true
        return
      }
      inFlight = true
      clearSyncTimer()
      try {
        const slots = await deviceService.getSchedule(creds.deviceId)

        // Gera um version hash simples baseado no conteúdo
        const version = await computeVersion(slots)
        const _prev = scheduleManager.getVersion()

        await scheduleManager.update(slots, version)
        usePlayerStore.getState().setOffline(false)

        // Força a recompilação da playlist para garantir que o loop inicie,
        // mesmo que a versão do schedule não tenha mudado.
        onUpdatedRef.current(version)

        if (usePlayerStore.getState().screen === 'starting') {
          usePlayerStore.getState().setScreen('playing')
        }
      } catch (err) {
        playerLogger.warn('[schedule-sync] Falha:', err)
        usePlayerStore.getState().setOffline(true)
        // Mantém o cache existente — não muda a tela
      } finally {
        inFlight = false
        if (!stopped) {
          if (rerunRequested) {
            rerunRequested = false
            void sync()
          } else {
            scheduleNextSync()
          }
        }
      }
    }

    function clearSyncTimer(): void {
      if (syncTimer) {
        clearTimeout(syncTimer)
        syncTimer = null
      }
    }

    function scheduleNextSync(): void {
      // Sempre no máximo um timer pendente
      clearSyncTimer()
      if (!stopped) {
        syncTimer = setTimeout(() => void sync(), SYNC_INTERVAL_MS + jitter())
      }
    }

    // Registrar callback para refetch forçado (via heartbeat shouldRefetchSchedule)
    const unregisterRefetch = registerRefetchCallback(() => void sync())

    // Sync inicial no mount (L-14): não depender do primeiro heartbeat bem-sucedido para ter agenda
    void sync()

    return () => {
      stopped = true
      clearSyncTimer()
      unregisterRefetch()
    }
  }, [])
}

async function computeVersion(slots: unknown): Promise<string> {
  const json = JSON.stringify(slots)
  const buf = new TextEncoder().encode(json)
  const hash = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(hash))
    .slice(0, 8)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
